/* =============================================================================
 *  Kreux Matrix — Executive Navigation Architecture
 *  5-pillar, click-triggered, multi-tiered top-nav command center.
 *  Replaces the inline hero-nav center links only (brand + lang toggle kept).
 *
 *  Guarantees:
 *   • Dropdowns open ONLY on click (no hover triggers).
 *   • Click-outside + Escape closes all menus.
 *   • Nested Level-2 menus render inline (mobile-safe, no flyout drift).
 *   • Anchor targets use existing DOM IDs verbatim (no invented anchors).
 *   • Bilingual EN/DE via useLang().
 * ===========================================================================*/

import { useState, useEffect, useRef } from "react";
import { Mail, Phone, Linkedin, Instagram, ChevronDown, ChevronRight } from "lucide-react";
import { useLang } from "@/lib/LangContext";

const dict = {
  en: {
    system: "System",
    architectureOverview: "Architecture Overview",
    roiAdvantage: "ROI & Advantage",
    intelligence: "Intelligence",
    ourCoreSpecialties: "Our Core Specialties",
    experienceMasterpieces: "Experience our masterpieces",
    experienceCore1: "Experience Humanoid Voice Agents",
    experienceCore2: "Experience the Digital Assistant",
    integration: "Integration",
    deploymentPhases: "The Deployment Phases",
    advantage: "Advantage",
    faqSimulator: "FAQ Simulator",
    legalCompliance: "Legal & Compliance",
    imprint: "Imprint",
    privacyPolicy: "Privacy Policy",
    termsConditions: "Terms & Conditions",
    initiate: "Initiate",
    initiateProtocol: "Initiate Protocol",
    directContacts: "Direct Contacts",
    email: "Email",
    phone: "Phone"
  },
  de: {
    system: "System",
    architectureOverview: "Architektur-Übersicht",
    roiAdvantage: "ROI & Vorteil",
    intelligence: "Intelligenz",
    ourCoreSpecialties: "Unsere Kern-Spezialgebiete",
    experienceMasterpieces: "Meisterwerke erleben",
    experienceCore1: "Humanoide Sprachagenten erleben",
    experienceCore2: "Den Digitalen Assistenten erleben",
    integration: "Integration",
    deploymentPhases: "Die Implementierungs-Phasen",
    advantage: "Vorteil",
    faqSimulator: "FAQ-Simulator",
    legalCompliance: "Recht & Compliance",
    imprint: "Impressum",
    privacyPolicy: "Datenschutzerklärung",
    termsConditions: "AGB",
    initiate: "Initiieren",
    initiateProtocol: "Protokoll initiieren",
    directContacts: "Direkter Kontakt",
    email: "E-Mail",
    phone: "Telefon"
  }
};

/* ─── Scroll helpers ──────────────────────────────────────────────────── */
const scrollTo = (selector) => {
  if (selector === "top") {
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }
  const el = document.querySelector(selector);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};
const scrollAndClick = (scrollSel, clickSel) => {
  scrollTo(scrollSel);
  setTimeout(() => {
    const btn = document.querySelector(clickSel);
    if (btn) btn.click();
  }, 850);
};

/* ─── Atoms ───────────────────────────────────────────────────────────── */
const PillarButton = ({ id, label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-expanded={active}
    aria-haspopup="menu"
    data-testid={`exec-pillar-${id}`}
    className={
      "exec-pillar-btn inline-flex items-center gap-1 px-3 py-2 text-[11px] font-semibold tracking-[0.22em] uppercase transition-colors duration-300 whitespace-nowrap " +
      (active ? "text-[var(--il-cyan)]" : "text-white/70 hover:text-white")
    }
  >
    {label}
    <ChevronDown
      className={"w-3.5 h-3.5 transition-transform duration-300 " + (active ? "rotate-180" : "rotate-0")}
      aria-hidden="true"
    />
  </button>
);

const Dropdown = ({ children, id, align = "left" }) => (
  <div
    role="menu"
    data-testid={`exec-dd-${id}`}
    className={
      "absolute top-full mt-3 min-w-[260px] rounded-xl border border-white/10 bg-[#0A1520]/95 backdrop-blur-2xl shadow-[0_20px_60px_-15px_rgba(0,229,255,0.18)] p-2 z-50 " +
      (align === "right" ? "right-0" : "left-0")
    }
  >
    {children}
  </div>
);

const DDItem = ({ label, onClick, hasSub, subOpen, testid }) => (
  <button
    type="button"
    role="menuitem"
    onClick={onClick}
    data-testid={testid}
    className={
      "w-full text-left flex items-center justify-between gap-6 px-3 py-2.5 rounded-lg text-[13px] transition-all duration-200 " +
      (subOpen
        ? "text-[var(--il-cyan)] bg-white/[0.06]"
        : "text-white/80 hover:text-[var(--il-cyan)] hover:bg-white/[0.04]")
    }
  >
    <span className="tracking-wide">{label}</span>
    {hasSub && (
      <ChevronRight
        className={"w-3.5 h-3.5 transition-transform duration-300 shrink-0 " + (subOpen ? "rotate-90" : "rotate-0")}
        aria-hidden="true"
      />
    )}
  </button>
);

const SubMenu = ({ testid, children }) => (
  <div
    role="menu"
    data-testid={testid}
    className="mt-1 mb-1 ml-3 pl-3 border-l border-[var(--il-cyan)]/25 py-1 space-y-1"
  >
    {children}
  </div>
);

const Pillar = ({ id, label, isOpen, onToggle, children, align }) => (
  <div className="relative" data-testid={`exec-cell-${id}`}>
    <PillarButton id={id} label={label} active={isOpen} onClick={onToggle} />
    {isOpen && <Dropdown id={id} align={align}>{children}</Dropdown>}
  </div>
);

/* ─── Direct-Contact visual info box (Level-2 for INITIATE) ───────────── */
const DirectContactsBox = () => (
  <div
    data-testid="exec-dd-initiate-contacts"
    className="mt-1 mb-1 ml-3 pl-3 border-l border-[var(--il-cyan)]/25 py-2 space-y-2.5"
  >
    <a
      href="mailto:intelligence@kreuxmatrix.de"
      data-testid="exec-contact-email"
      className="flex items-center gap-2.5 px-2 py-1.5 rounded-md text-[13px] text-white/80 hover:text-[var(--il-cyan)] hover:drop-shadow-[0_0_8px_var(--il-cyan)] transition-all duration-300 no-underline"
    >
      <Mail className="w-4 h-4 shrink-0" aria-hidden="true" />
      <span>intelligence@kreuxmatrix.de</span>
    </a>
    <a
      href="tel:+4917685984847"
      data-testid="exec-contact-phone"
      className="flex items-center gap-2.5 px-2 py-1.5 rounded-md text-[13px] text-white/80 hover:text-[var(--il-cyan)] hover:drop-shadow-[0_0_8px_var(--il-cyan)] transition-all duration-300 no-underline"
    >
      <Phone className="w-4 h-4 shrink-0" aria-hidden="true" />
      <span>+49 176 85984847</span>
    </a>
    <div className="flex items-center gap-3 pt-1 px-2">
      <a
        href="https://www.linkedin.com/company/136074047/"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="LinkedIn"
        data-testid="exec-contact-linkedin"
        className="text-white/60 hover:text-[var(--il-cyan)] hover:drop-shadow-[0_0_8px_var(--il-cyan)] transition-all duration-300 inline-flex"
      >
        <Linkedin className="w-4 h-4" aria-hidden="true" />
      </a>
      <a
        href="https://www.instagram.com/kreuxmatrixaistudio/"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Instagram"
        data-testid="exec-contact-instagram"
        className="text-white/60 hover:text-[var(--il-cyan)] hover:drop-shadow-[0_0_8px_var(--il-cyan)] transition-all duration-300 inline-flex"
      >
        <Instagram className="w-4 h-4" aria-hidden="true" />
      </a>
    </div>
  </div>
);

/* ─── Main Nav ────────────────────────────────────────────────────────── */
const ExecutiveNav = ({ onOpenArch }) => {
  const { lang } = useLang();
  const t = dict[lang] || dict.en;
  const [openPillar, setOpenPillar] = useState(null);
  const [openSub, setOpenSub] = useState(null);
  const navRef = useRef(null);

  useEffect(() => {
    const handleDocClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenPillar(null);
        setOpenSub(null);
      }
    };
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setOpenPillar(null);
        setOpenSub(null);
      }
    };
    document.addEventListener("mousedown", handleDocClick);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleDocClick);
      document.removeEventListener("keydown", handleEsc);
    };
  }, []);

  const togglePillar = (id) => {
    setOpenSub(null);
    setOpenPillar((prev) => (prev === id ? null : id));
  };
  const toggleSub = (id) => setOpenSub((prev) => (prev === id ? null : id));
  const closeAll = () => { setOpenPillar(null); setOpenSub(null); };
  const act = (fn) => () => { fn(); closeAll(); };

  return (
    <nav
      ref={navRef}
      data-testid="exec-nav"
      className="exec-nav flex items-center gap-1 flex-wrap justify-center"
      style={{ "--il-cyan": "#00E5FF" }}
    >
      {/* 1. SYSTEM */}
      <Pillar id="system" label={t.system} isOpen={openPillar === "system"} onToggle={() => togglePillar("system")}>
        <DDItem label={t.architectureOverview} onClick={act(() => scrollTo("top"))} testid="exec-dd-system-arch" />
        <DDItem label={t.roiAdvantage} onClick={act(() => scrollTo("#performance-layer"))} testid="exec-dd-system-roi" />
      </Pillar>

      {/* 2. INTELLIGENCE */}
      <Pillar id="intelligence" label={t.intelligence} isOpen={openPillar === "intelligence"} onToggle={() => togglePillar("intelligence")}>
        <DDItem label={t.ourCoreSpecialties} onClick={act(() => scrollTo("#matrix-section-cores"))} testid="exec-dd-int-cores" />
        <DDItem
          label={t.experienceMasterpieces}
          onClick={() => toggleSub("intel-masterpieces")}
          hasSub
          subOpen={openSub === "intel-masterpieces"}
          testid="exec-dd-int-masterpieces"
        />
        {openSub === "intel-masterpieces" && (
          <SubMenu testid="exec-sub-int-masterpieces">
            <DDItem
              label={t.experienceCore1}
              onClick={act(() => window.dispatchEvent(new CustomEvent("kreux:open-core", { detail: 0 })))}
              testid="exec-dd-int-core-1"
            />
            <DDItem
              label={t.experienceCore2}
              onClick={act(() => window.dispatchEvent(new CustomEvent("kreux:launch-logic-demo")))}
              testid="exec-dd-int-core-2"
            />
          </SubMenu>
        )}
      </Pillar>

      {/* 3. INTEGRATION */}
      <Pillar id="integration" label={t.integration} isOpen={openPillar === "integration"} onToggle={() => togglePillar("integration")}>
        <DDItem label={t.deploymentPhases} onClick={act(() => scrollTo("#integration-layer"))} testid="exec-dd-integration-phases" />
      </Pillar>

      {/* 4. ADVANTAGE */}
      <Pillar id="advantage" label={t.advantage} isOpen={openPillar === "advantage"} onToggle={() => togglePillar("advantage")}>
        <DDItem label={t.faqSimulator} onClick={act(() => scrollTo("#faq"))} testid="exec-dd-adv-faq" />
        <DDItem
          label={t.legalCompliance}
          onClick={() => toggleSub("adv-legal")}
          hasSub
          subOpen={openSub === "adv-legal"}
          testid="exec-dd-adv-legal"
        />
        {openSub === "adv-legal" && (
          <SubMenu testid="exec-sub-adv-legal">
            <a
              href="/imprint"
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeAll}
              data-testid="exec-dd-adv-imprint"
              className="w-full text-left flex items-center gap-6 px-3 py-2.5 rounded-lg text-[13px] transition-all duration-200 text-white/80 hover:text-[var(--il-cyan)] hover:bg-white/[0.04] no-underline"
            >
              <span className="tracking-wide">{t.imprint}</span>
            </a>
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeAll}
              data-testid="exec-dd-adv-privacy"
              className="w-full text-left flex items-center gap-6 px-3 py-2.5 rounded-lg text-[13px] transition-all duration-200 text-white/80 hover:text-[var(--il-cyan)] hover:bg-white/[0.04] no-underline"
            >
              <span className="tracking-wide">{t.privacyPolicy}</span>
            </a>
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeAll}
              data-testid="exec-dd-adv-terms"
              className="w-full text-left flex items-center gap-6 px-3 py-2.5 rounded-lg text-[13px] transition-all duration-200 text-white/80 hover:text-[var(--il-cyan)] hover:bg-white/[0.04] no-underline"
            >
              <span className="tracking-wide">{t.termsConditions}</span>
            </a>
          </SubMenu>
        )}
      </Pillar>

      {/* 5. INITIATE */}
      <Pillar id="initiate" label={t.initiate} isOpen={openPillar === "initiate"} onToggle={() => togglePillar("initiate")} align="right">
        <DDItem
          label={t.initiateProtocol}
          onClick={act(() => onOpenArch?.())}
          testid="exec-dd-initiate-protocol"
        />
        <DDItem
          label={t.directContacts}
          onClick={() => toggleSub("init-contacts")}
          hasSub
          subOpen={openSub === "init-contacts"}
          testid="exec-dd-initiate-directcontacts"
        />
        {openSub === "init-contacts" && <DirectContactsBox />}
      </Pillar>
    </nav>
  );
};

export default ExecutiveNav;
